import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabaseClient';

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY;

export function usePushNotifications(userId) {
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState('default');
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) return;
    if (!userId) return;

    // Check current permission status
    const checkPermission = () => {
      const permission = Notification.permission;
      setPermissionStatus(permission);
      
      if (permission === 'default') {
        // Show our custom modal instead of requesting immediately
        setShowPermissionModal(true);
      } else if (permission === 'granted') {
        // User already granted permission, proceed with subscription
        registerPushSubscription();
      }
      // If denied, we don't show modal or request again
    };

    checkPermission();
  }, [userId]);

  const registerPushSubscription = async () => {
    try {
      setError(null);
      
      // Check if service worker is available
      if (!navigator.serviceWorker) {
        throw new Error('Service workers are not supported');
      }

      // Register service worker
      const registration = await navigator.serviceWorker.register('/sw.js');
      
      // Check if push manager is available
      if (!registration.pushManager) {
        throw new Error('Push messaging is not supported');
      }

      // Check if already subscribed
      const existingSubscription = await registration.pushManager.getSubscription();
      if (existingSubscription) {
        // Save existing subscription
        await saveSubscription(existingSubscription);
        return;
      }
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });
      await saveSubscription(subscription);

    } catch (err) {
      setError('Notification registration could not be completed.');
      console.warn('Notification registration service unavailable.');
    }
  };

  const saveSubscription = async (subscription) => {
    try {
      
      // Use the updated save-subscription function
      const { data, error } = await supabase.functions.invoke('save-subscription', {
        body: { userId, subscription: subscription.toJSON() }
      });

      if (error) {
        throw error;
      } else {
      }
    } catch (err) {
      setError('Notification registration could not be completed.');
      throw err;
    }
  };

  const handlePermissionGranted = () => {
    setPermissionStatus('granted');
    setShowPermissionModal(false);
    registerPushSubscription();
  };

  // Allow manual re-subscription (e.g., if VAPID key changes or endpoint becomes invalid)
  const resubscribe = async () => {
    try {
      if (!navigator.serviceWorker) throw new Error('Service workers unsupported');
      const registration = await navigator.serviceWorker.getRegistration('/sw.js');
      const existing = await registration?.pushManager.getSubscription();
      if (existing) {
        await existing.unsubscribe();
      }
      await registerPushSubscription();
    } catch (err) {
    }
  };

  return {
    showPermissionModal,
    setShowPermissionModal,
    permissionStatus,
    error,
    handlePermissionGranted,
    resubscribe
  };
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map(char => char.charCodeAt(0)));
}
